import { Show } from '@clerk/react'
import type { SessionResponse } from '@schemas/session'
import { lazy, Suspense } from 'react'
import { Outlet, useLoaderData, useNavigation } from 'react-router'
import { PanelLoader } from '../components/loading/panel-loader.tsx'
import { IndeterminateRule } from '../components/loading/rally-loader.tsx'
import { ROUTE_LOADER_GATE_MS, useDelayedLoading } from '../components/loading/use-delayed-loading.ts'
import { Nav } from '../components/nav.tsx'
import { ReauthBanner } from '../components/reauth-banner.tsx'
import { ShellHeader } from '../components/shell-header.tsx'
import { UpdateAvailableModal } from '../components/update-available-modal.tsx'
import { useCanManage } from '../lib/can-manage.ts'
import { useVersionCheck } from '../lib/use-version-check.ts'

// Lazy: this was previously a static import, which folded the entire marketing page
// (Hero, PricingTiers, WeekGrid, etc.) into the main entry chunk for every visitor,
// including already-signed-in dashboard users who never render it (RAL-140).
const Landing = lazy(() => import('./landing.tsx'))

// Dark full-screen shell: at lg+ a fixed 232px sidebar column beside a fluid
// main column; below lg the nav compacts into a top bar and main stacks under
// it. Secondary routes keep their existing page bodies inside <main>; their
// per-page re-theme is deliberate follow-up work.
//
// <main> renders before <Nav> in the DOM so keyboard tab order reaches page
// content first, matching the disconnected-console design spec's stated tab
// order (RAL-32) — this was previously untrue on every route, not just the
// disconnected console; see the spec's "Correction (RAL-32 planning)" note.
// Nav's `order-first` class (nav.tsx) keeps it visually first (left on
// desktop, top on mobile) despite following <main> in the DOM.
export default function RootLayout() {
  const session = useLoaderData() as SessionResponse
  const canReconnect = useCanManage()
  const versionCheck = useVersionCheck()
  // RAL-182 route-change surface: the shell never unmounts, so a navigation shows a 2px
  // indeterminate rule overlaying main's top edge (absolute — adding height would shift the
  // canvas on every navigation) and dims the outgoing content instead of blanking it. 150ms
  // gate: route changes are expected to be fast and an early hairline costs nothing.
  //
  // RAL-188 widens this from 'loading' to any non-idle state, so a form submission gets the
  // same treatment. That covers every mutation routed through react-router — the seven setup
  // steps and the review actions — in one place, instead of an in-flight affordance bolted
  // onto each button. A mark inside a button would change its width as it appears; the shell
  // rule carries the motion without touching layout anywhere.
  const navigation = useNavigation()
  const inFlight = useDelayedLoading(navigation.state !== 'idle', ROUTE_LOADER_GATE_MS)
  // formData (not state === 'submitting') so the label holds through the revalidation that
  // follows a submit — mid-flight it would otherwise flip to "Switching view".
  const inFlightLabel = navigation.formData ? 'Saving' : 'Switching view'
  return (
    <>
      <Show when="signed-out">
        <Suspense fallback={<PanelLoader label="Loading workspace" minHeight={200} className="p-0" />}>
          <Landing />
        </Suspense>
      </Show>
      <Show when="signed-in">
        <div className="flex min-h-dvh flex-col bg-ground-void text-ink-primary lg:grid lg:grid-cols-[232px_minmax(0,1fr)]">
          <main className="bg-blueprint relative min-w-0 flex-1 px-6 py-8 lg:px-10">
            {inFlight ? <IndeterminateRule className="absolute inset-x-0 top-0" /> : null}
            {/* A submission leaves its own form on screen at full strength — dimming the canvas
                under the user's own edit reads as the page going away, not as work in flight. */}
            <div
              className="transition-opacity duration-150 ease-linear"
              style={{ opacity: inFlight && !navigation.formData ? 0.4 : 1 }}
            >
              <ShellHeader session={session} />
              <ReauthBanner missingScopes={session.missingScopes} canReconnect={canReconnect} />
              <Outlet />
            </div>
          </main>
          <Nav session={session} inFlightLabel={inFlight ? inFlightLabel : null} />
        </div>
      </Show>
      <UpdateAvailableModal
        open={versionCheck.updateAvailable}
        onReload={versionCheck.reload}
        onDismiss={versionCheck.dismiss}
      />
    </>
  )
}
